import { Box, Icon, Image } from '@zillow/constellation-10';
import { IconDollarSignCircleFilled, IconFileFilled } from '@zillow/constellation-icons';

import { useRentalManagerRenterContactWebApiHandler_getTenantsRequestedToUsePayment } from '@zg-rentals/rental-manager-env';

import Bone from '../../../Bone';
import CardSkeleton from './CardSkeleton';
import NextStepCard from './NextStepCard';
import FriendlyAgentIllustration from './FriendlyAgentIllustration';
import TenantDirectoryCSEvents from '../../../../model/ClickstreamEvent/TenantDirectoryCSEvents';

const { LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE } = TenantDirectoryCSEvents;

type UpsellStepProps = {
  listingAlias: string;
  onCTAClick: (options?: { triggerUpsellCloseEvent?: boolean }) => void;
  onClickstreamEvent: (
    triggerSource: (typeof LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE)[keyof typeof LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE],
  ) => void;
};

const getPaymentsCardContent = (tenantRequestedPayments: boolean) => {
  if (tenantRequestedPayments) {
    return {
      title: 'Your tenants requested to use Zillow Payments',
      description: 'Get paid with more predictability',
    };
  }

  return {
    title: 'Set up Zillow Payments',
    description: 'Collect rent and other payments online — for free.',
  };
};

const UpsellStep = ({ listingAlias, onCTAClick, onClickstreamEvent }: UpsellStepProps) => {
  // check if tenants have requested to use the payment feature
  const { data: tenantsRequestedToUsePaymentResponse, isLoading: isLoadingTenantsRequestedToUsePayment } =
    useRentalManagerRenterContactWebApiHandler_getTenantsRequestedToUsePayment({
      listingAlias,
    });

  const paymentsCardContent = getPaymentsCardContent(tenantsRequestedToUsePaymentResponse ?? false);

  const handleLeaseClick = () => {
    onClickstreamEvent(LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE.CREATE_LEASE);
    onCTAClick();
  };

  const handlePaymentsClick = () => {
    onClickstreamEvent(LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE.SETUP_PAYMENTS);
    onCTAClick();
  };

  if (isLoadingTenantsRequestedToUsePayment) {
    return (
      <Box data-testid="UpsellStep-loading" css={{ display: 'flex', flexDirection: 'column', gap: '600' }}>
        <Box css={{ display: 'flex', justifyContent: 'flex-start' }}>
          <Box css={{ width: '160px', height: '160px' }}>
            <Bone type="cover" />
          </Box>
        </Box>

        <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
          <CardSkeleton />
          <CardSkeleton />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="UpsellStep"
      data-eventid={TenantDirectoryCSEvents.LeaseAndPaymentUpsellStepClick.data_event_id}
      css={{ display: 'flex', flexDirection: 'column', gap: '600' }}
    >
      <Box css={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Image asChild alt="Friendly agent illustration">
          <FriendlyAgentIllustration width={160} height={160} />
        </Image>
      </Box>

      <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
        <NextStepCard
          data-testid="CreateLeaseCard"
          icon={
            <Icon size="lg" css={{ color: '{colors.icon.express.trust.strong}' }}>
              <IconFileFilled />
            </Icon>
          }
          title="Create or upload a lease agreement"
          description="Customize the lease for your needs, or upload your own."
          buttonLabel="Create or upload lease"
          buttonEmphasis="filled"
          href={`/rental-manager/leasing/property/${listingAlias}/create`}
          onClick={handleLeaseClick}
        />

        <NextStepCard
          data-testid="SetUpPaymentsCard"
          icon={
            <Icon size="lg" css={{ color: '{colors.icon.express.trust.strong}' }}>
              <IconDollarSignCircleFilled />
            </Icon>
          }
          title={paymentsCardContent.title}
          description={paymentsCardContent.description}
          buttonLabel="Set up payments"
          buttonEmphasis="outlined"
          href={`/rental-manager/payments/properties/${listingAlias}`}
          onClick={handlePaymentsClick}
        />
      </Box>
    </Box>
  );
};

export default UpsellStep;
